import { Button, Combobox, Hint, Label } from 'components';
import { useLocale, useTranslations } from 'next-intl';
import { FC, Fragment, useCallback, useEffect, useMemo } from 'react';

import { ArrowRightIcon, PlusIcon } from 'lib/svgs/Icons';

import { useSearchParams } from 'next/navigation';
import { useHubspotIntegrationTableColumns } from 'providers/HubSpotIntegrationProviders/useHubspotIntegrationTableColumns';
import { connect } from 'react-redux';
import { KeysEnum } from 'types/KeysEnum';
import { Option } from 'types/Option';
import { availableLanguageOptions } from 'utils/availableLanguageOptions';
import { cn } from 'utils/cn';
import { extractParam } from 'utils/paramsUtil';

import {
  addAllToExcludedRanges,
  addToExcludedRanges,
  removeColumnMapping,
  removeExcludedColumns,
  setColumnMapping,
  updateColumnMapping,
  updateTargetColumnMapping
} from './_actions';
import {
  getColumns,
  getMostUsedTargetLanguages,
  getSelectedFile,
  getTotalColumns,
  getTotalRows
} from './_selectors';

type TargetMapping = {
  column: string;
  language: string;
};

export type ColumnMapping = {
  sourceColumn: string;
  targetsColumn: TargetMapping[];
};

type Range = { column: number; row: number };

type ExcludedCells = { start: Range; end: Range };

interface IHubDbColumnMapping {
  targetLanguageCodes: string[];
  columnMapping: ColumnMapping[];
  mostUsedTargetLanguages: string[];
  excludedLanguageCodes: string[];
  columns: number[];
  totalColumns: number;
  totalRows: number;
  addToExcludedRanges: (range: ExcludedCells) => void;
  addAllToExcludedRanges: (ranges: ExcludedCells[]) => void;
  removeExcludedColumns: (columns: number[]) => void;
  setColumnMapping: (column: ColumnMapping) => void;
  removeColumnMapping: (index: number) => void;
  updateColumnMapping: (index: number, column: ColumnMapping) => void;
  updateTargetColumnMapping: (index: number, targetIndex: number, column: TargetMapping) => void;
}

const HubDbColumnMapping: FC<IHubDbColumnMapping> = ({
  targetLanguageCodes,
  columnMapping,
  mostUsedTargetLanguages,
  excludedLanguageCodes,
  columns,
  totalColumns,
  totalRows,
  addToExcludedRanges,
  addAllToExcludedRanges,
  setColumnMapping,
  removeColumnMapping,
  updateColumnMapping,
  updateTargetColumnMapping,
  removeExcludedColumns
}) => {
  const locale = useLocale();
  const t = useTranslations();

  const searchParams = useSearchParams();
  const ids = extractParam(searchParams, KeysEnum.Ids, []) as string[];
  const tableId = ids?.at(0) ?? '';

  const { data } = useHubspotIntegrationTableColumns({ tableId }, { enabled: !!tableId });

  const columnsOptions = useMemo(
    () =>
      data?.map((col, index) => ({
        label: String.fromCharCode(66 + index),
        value: col
      })),
    [data]
  );

  const columnsMapped = useMemo(
    () =>
      columnMapping.reduce<number[]>((acc, col) => {
        const columnIndex =
          columnsOptions?.findIndex((option) => option.value === col.sourceColumn) ?? -1;
        return columnIndex !== -1 ? [...acc, columnIndex + 2] : acc;
      }, []),
    [columnMapping, columnsOptions]
  );

  const languageOptions = availableLanguageOptions(
    targetLanguageCodes,
    excludedLanguageCodes,
    mostUsedTargetLanguages,
    undefined,
    t,
    locale
  );

  const addAllExcludedRangesColumns = useCallback(() => {
    const excludedRanges = Array.from({ length: totalColumns }, (_, i) => {
      const col = i + 1;

      return {
        start: { column: col, row: 1 },
        end: { column: col, row: totalRows }
      };
    });

    addAllToExcludedRanges([
      { start: { column: 1, row: 1 }, end: { column: totalColumns, row: 1 } },
      ...excludedRanges
    ]);
  }, [addAllToExcludedRanges, totalColumns, totalRows]);

  const handlerAddColumn = () =>
    setColumnMapping({
      sourceColumn: '',
      targetsColumn: [
        {
          column: '',
          language: ''
        }
      ]
    });

  const handlerRemoveColumn = (index: number) => () => removeColumnMapping(index);

  const onSelectSourceColumn = (index: number) => (option: Option) =>
    updateColumnMapping(index, {
      sourceColumn: option.value as string
    } as ColumnMapping);

  const onSelectTarget =
    (index: number, targetIndex: number, type: 'column' | 'language') => (option: Option) =>
      updateTargetColumnMapping(index, targetIndex, {
        [type]: (option?.value as string) ?? ''
      } as TargetMapping);

  useEffect(() => {
    if (totalColumns && totalRows) {
      addAllExcludedRangesColumns();
    }
  }, [addToExcludedRanges, columns, addAllExcludedRangesColumns, totalColumns, totalRows]);

  useEffect(() => {
    if (columnsMapped?.length) removeExcludedColumns(columnsMapped);
  }, [columnsMapped, removeExcludedColumns]);

  return (
    <>
      <div className="mb-3">
        <Label>{t('ordering_form.language_columns.column_mapping')}</Label>
        <div className="mb-2 flex flex-col gap-2 rounded-b ">
          {columnMapping.map((column, index) => (
            <div
              key={column.sourceColumn + index}
              className={cn('flex flex-col  rounded transition-all ease-linear')}
            >
              <div className="relative flex gap-1.5">
                <Combobox
                  onChange={onSelectSourceColumn(index)}
                  wideHeight
                  noClose
                  placeholder={t('placeholders.column')}
                  options={columnsOptions}
                  selectedValue={columnMapping?.[index]?.sourceColumn || ''}
                  classNameContainerInput="!w-[90px]"
                />
                <div className="mb-2 flex flex-col items-center justify-center">
                  <ArrowRightIcon className="scale-[1.6] !text-tertiary-200" />
                </div>
                {column.targetsColumn?.map((target, j) => (
                  <Fragment key={index + j}>
                    <Combobox
                      onChange={onSelectTarget(index, j, 'column')}
                      wideHeight
                      noClose
                      placeholder={t('placeholders.column')}
                      options={columnsOptions}
                      selectedValue={columnMapping?.[index]?.targetsColumn?.[j]?.column || ''}
                      classNameContainerInput="!w-[90px]"
                      classNameInput="capitalize"
                    />
                    <Combobox
                      onChange={onSelectTarget(index, j, 'language')}
                      wideHeight
                      noClose
                      placeholder={t('placeholders.language')}
                      selectedValue={columnMapping?.[index]?.targetsColumn?.[j]?.language || ''}
                      classNameInput="!w-full capitalize"
                      className="!w-full"
                      options={languageOptions}
                    />
                    <span
                      className={cn(
                        'absolute -right-5 top-3',
                        'ticon-close r-5 cursor-pointer pb-2 pl-5',
                        'text-tertiary-500 transition-all duration-200 hover:text-secondary-red',
                        columnMapping?.length === 1 && 'hidden'
                      )}
                      onClick={handlerRemoveColumn(index)}
                    />
                  </Fragment>
                ))}
              </div>
            </div>
          ))}
          <Hint>{t('hints.select_source_column')}</Hint>
          <Button
            label={t('ordering_form.language_columns.add_another_column')}
            startAdornment={<PlusIcon className="!text-white" />}
            variant="contained"
            color="secondary"
            colorIcon="white"
            size="small"
            onClick={handlerAddColumn}
            className="!m-0 !flex !justify-start !pb-0 !pl-4"
          />
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: {
    staticData: any;
    inputs: {
      sourceLanguageCode: string;
      targetLanguageCodes: string[];
    };
    columnMapping: any;
  }) => {
    return {
      columnMapping: state?.columnMapping,
      selectedFile: getSelectedFile(state),
      excludedLanguageCodes: [
        ...(state?.inputs.targetLanguageCodes ?? []),
        state?.inputs.sourceLanguageCode
      ],
      targetLanguageCodes: state?.staticData?.targetLanguageCodes,
      mostUsedTargetLanguages: getMostUsedTargetLanguages(state),
      totalRows: getTotalRows(state),
      totalColumns: getTotalColumns(state),
      columns: getColumns(state)
    };
  },
  {
    setColumnMapping,
    removeColumnMapping,
    updateColumnMapping,
    updateTargetColumnMapping,
    addToExcludedRanges,
    addAllToExcludedRanges,
    removeExcludedColumns
  }
)(HubDbColumnMapping);
